import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Plus, Send, Users, Sparkles, Wifi, WifiOff, Search, Check, X, MessageSquareText, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useThemeClasses } from '@/hooks/useTheme';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export default function GCPage() {
  const navigate = useNavigate();
  const { user, sessionToken } = useAuth();
  const { isDark, textClass, textMutedClass, textVeryMutedClass, borderClass, hoverBgClass, bgActiveClass } = useThemeClasses();
  const [gcs, setGcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newGCName, setNewGCName] = useState('');
  const [activeGC, setActiveGC] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  
  // User selection for new GC
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const [startingSidebar, setStartingSidebar] = useState(null); // user_id of person we're starting sidebar with
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Start a sidebar (private chat) with someone from within the GC
  const startSidebarWith = async (targetUser) => {
    if (!targetUser || startingSidebar) return;
    
    setStartingSidebar(targetUser.user_id);
    try {
      const response = await axios.post(
        `${API}/sidebar/create?other_user_id=${targetUser.user_id}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Starting sidebar with ${targetUser.name}...`);
      navigate(`/sidebar/${response.data.sidebar_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start sidebar');
    } finally {
      setStartingSidebar(null);
    }
  };

  useEffect(() => {
    fetchGCs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeGC) {
      fetchMessages(activeGC.gc_id);
      connectWebSocket(activeGC.gc_id);
    }
    
    return () => {
      disconnectWebSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGC]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch available users when create dialog opens
  useEffect(() => {
    if (createOpen) {
      fetchAvailableUsers();
    } else {
      // Reset state when dialog closes
      setSelectedUsers([]);
      setNewGCName('');
      setUserSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen]);

  const fetchAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/gc/available-users`, { withCredentials: true });
      setAvailableUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  // WebSocket Connection
  const connectWebSocket = useCallback((gcId) => {
    if (!sessionToken || !gcId) return;
    
    disconnectWebSocket();
    
    try {
      const wsUrl = `${WS_URL}/ws/gc/${gcId}?token=${sessionToken}`;
      console.log('[WS] Connecting to:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('[WS] Connected to GC:', gcId);
        setWsConnected(true);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[WS] Received:', data);
          
          if (data.type === 'new_message') {
            setMessages(prev => {
              // Dedupe by real message_id
              if (prev.some(m => m.message_id === data.message.message_id)) {
                return prev;
              }
              // Replace optimistic message (our own echo) — match by content + user
              const optimistic = prev.find(m =>
                m.message_id?.startsWith('temp-') &&
                m.content === data.message.content &&
                m.user_id === data.message.user_id
              );
              if (optimistic) {
                return prev.map(m =>
                  m.message_id === optimistic.message_id
                    ? { ...data.message, _status: 'sent' }
                    : m
                );
              }
              return [...prev, data.message];
            });
          } else if (data.type === 'typing') {
            if (data.user_id !== user?.user_id) {
              setTypingUsers(prev => {
                if (!prev.includes(data.username)) {
                  return [...prev, data.username];
                }
                return prev;
              });
              setTimeout(() => {
                setTypingUsers(prev => prev.filter(u => u !== data.username));
              }, 3000);
            }
          }
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };
      
      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
        setWsConnected(false);
      };
      
      ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        setWsConnected(false);
        
        if (event.code !== 1000 && activeGC?.gc_id === gcId) {
          setTimeout(() => {
            console.log('[WS] Attempting reconnect...');
            connectWebSocket(gcId);
          }, 3000);
        }
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('[WS] Connection error:', error);
      setWsConnected(false);
    }
  }, [sessionToken, user?.user_id, activeGC?.gc_id]);

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'User navigated away');
      wsRef.current = null;
    }
    setWsConnected(false);
  };

  const sendTypingIndicator = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  }, []);

  const fetchGCs = async () => {
    try {
      const response = await axios.get(`${API}/gc/my-gcs`, { withCredentials: true });
      setGcs(response.data);
      
      if (response.data.length > 0 && !activeGC) {
        selectGC(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching GCs:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectGC = async (gc) => {
    try {
      const response = await axios.get(`${API}/gc/${gc.gc_id}`, { withCredentials: true });
      setActiveGC(response.data);
    } catch (error) {
      toast.error('Failed to load GC');
    }
  };

  const fetchMessages = async (gcId) => {
    try {
      const response = await axios.get(`${API}/gc/${gcId}/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeGC || sendingMessage) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setSendingMessage(true);

    // Optimistic message with temp ID
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg = {
      message_id: tempId,
      user_id: user?.user_id,
      content: messageContent,
      created_at: new Date().toISOString(),
      user: { name: user.name, username: user.username, picture: user.picture },
      _status: 'sending',
    };
    setMessages(prev => [...prev, optimisticMsg]);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({
          type: 'message',
          content: messageContent
        }));
        // Mark sent — the WS echo will deduplicate via content match
        setMessages(prev => prev.map(m => m.message_id === tempId ? { ...m, _status: 'sent' } : m));
      } catch {
        setMessages(prev => prev.map(m => m.message_id === tempId ? { ...m, _status: 'failed' } : m));
      }
      setSendingMessage(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/gc/${activeGC.gc_id}/message`,
        null,
        {
          params: { content: messageContent },
          withCredentials: true
        }
      );
      // Replace optimistic msg with real one
      setMessages(prev => prev.map(m =>
        m.message_id === tempId
          ? { ...response.data, user: { name: user.name, username: user.username, picture: user.picture }, _status: 'sent' }
          : m
      ));
    } catch (error) {
      setMessages(prev => prev.map(m => m.message_id === tempId ? { ...m, _status: 'failed' } : m));
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const retryMessage = async (failedMsg) => {
    // Remove failed message
    setMessages(prev => prev.filter(m => m.message_id !== failedMsg.message_id));
    // Re-send
    setNewMessage(failedMsg.content);
    setTimeout(() => {
      const input = document.querySelector('[data-testid="gc-message-input"]');
      if (input) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
        nativeInputValueSetter.call(input, failedMsg.content);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, 50);
  };

  const askBonitaInGC = async () => {
    if (!newMessage.trim() || !activeGC) return;
    
    setSendingMessage(true);
    try {
      const response = await axios.post(
        `${API}/gc/${activeGC.gc_id}/bonita`,
        null,
        { 
          params: { question: newMessage.trim() },
          withCredentials: true 
        }
      );
      
      setMessages([...messages, { ...response.data, user: { name: 'Bonita', username: 'bonita' } }]);
      setNewMessage('');
    } catch (error) {
      toast.error('Bonita is unavailable');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleMessageChange = (e) => {
    setNewMessage(e.target.value);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(sendTypingIndicator, 500);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      }
      return [...prev, userId];
    });
  };

  const createGC = async () => {
    if (!newGCName.trim()) {
      toast.error('Please enter a name for the chat');
      return;
    }
    
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one person to chat with');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/gc/create`, {
        name: newGCName.trim(),
        member_ids: selectedUsers
      }, { withCredentials: true });
      
      toast.success('Chat started!');
      setCreateOpen(false);
      fetchGCs();
      selectGC(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create chat');
    }
  };

  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: false });
    } catch {
      return '';
    }
  };

  // Filter users by search
  const filteredUsers = availableUsers.filter(u => 
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.username?.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="mb-safe h-[calc(100vh-8rem)] md:h-screen flex flex-col" data-testid="gc-page">
      {/* Header */}
      <div className={cn("sticky top-14 md:top-0 z-30 glass border-b p-4 flex-shrink-0", borderClass)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageCircle className={cn("h-5 w-5", textClass)} />
            <h1 className={cn("font-display text-sm tracking-widest uppercase", textClass)}>The GC</h1>
            {wsConnected ? (
              <span className="flex items-center gap-1 text-[10px] text-green-400">
                <Wifi className="h-3 w-3" />
                Live
              </span>
            ) : (
              <span className={cn("flex items-center gap-1 text-[10px]", isDark ? "text-white/30" : "text-gray-400")}>
                <WifiOff className="h-3 w-3" />
              </span>
            )}
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            size="sm"
            className={cn("rounded-none text-xs font-display tracking-wider", isDark ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-black/90")}
            data-testid="create-gc-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-2" />
            Start Chat
          </Button>
        </div>
        <p className={cn("text-[10px] mt-2", textVeryMutedClass)}>"The Chat Said..."</p>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* GC List */}
        <div className={cn(
          "border-r flex-shrink-0",
          borderClass,
          activeGC ? "hidden md:block w-64" : "w-full md:w-64"
        )}>
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : gcs.length === 0 ? (
            <div className="text-center py-16 px-4">
              <MessageCircle className={cn("h-10 w-10 mx-auto mb-4", isDark ? "text-white/20" : "text-gray-300")} />
              <p className={cn("text-sm mb-2", textMutedClass)}>No chats yet</p>
              <p className={cn("text-xs", isDark ? "text-white/30" : "text-gray-400")}>Start a chat to connect with someone</p>
            </div>
          ) : (
            <ScrollArea className="h-full">
              <div className={cn("divide-y", borderClass)}>
                {gcs.map((gc) => (
                  <div
                    key={gc.gc_id}
                    onClick={() => selectGC(gc)}
                    className={cn(
                      "p-4 cursor-pointer transition-colors",
                      activeGC?.gc_id === gc.gc_id ? bgActiveClass : hoverBgClass
                    )}
                    data-testid={`gc-${gc.gc_id}`}
                  >
                    <p className={cn("font-medium text-sm truncate", textClass)}>{gc.name}</p>
                    <p className={cn("text-xs flex items-center gap-1 mt-1", textVeryMutedClass)}>
                      <Users className="h-3 w-3" />
                      {gc.members?.length || 1} members
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Chat Area */}
        {activeGC ? (
          <div className="flex-1 flex flex-col min-w-0">
            {/* Chat Header */}
            <div className={cn("p-4 border-b flex items-center justify-between flex-shrink-0", borderClass)}>
              <div>
                <h2 className={cn("font-medium text-sm", textClass)}>{activeGC.name}</h2>
                <p className={cn("text-[10px]", textVeryMutedClass)}>{activeGC.members?.length} members</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className={cn("md:hidden", textMutedClass)}
                onClick={() => setActiveGC(null)}
              >
                Back
              </Button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <p className={cn("text-sm", textVeryMutedClass)}>No messages yet</p>
                  <p className={cn("text-xs", isDark ? "text-white/30" : "text-gray-400")}>Start the conversation</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div 
                      key={msg.message_id}
                      className={cn(
                        "flex gap-3",
                        msg.user_id === user?.user_id && "flex-row-reverse"
                      )}
                    >
                      {/* Avatar - clickable for other users to start sidebar */}
                      {msg.user_id !== user?.user_id && msg.user_id !== 'bonita' ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-white/30 rounded-full">
                              <Avatar className="h-8 w-8 cursor-pointer hover:ring-2 hover:ring-white/30 transition-all">
                                <AvatarImage src={msg.user?.picture} />
                                <AvatarFallback className="bg-white/10 text-[10px]">
                                  {msg.user?.name?.charAt(0) || '?'}
                                </AvatarFallback>
                              </Avatar>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent 
                            className={cn("w-48 p-2", isDark ? "bg-zinc-900 border-white/10" : "bg-white border-gray-200")}
                            side="right"
                            align="start"
                          >
                            <div className="space-y-1">
                              <p className={cn("text-xs font-medium px-2 py-1", textClass)}>
                                {msg.user?.name}
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startSidebarWith(msg.user)}
                                disabled={startingSidebar === msg.user?.user_id}
                                className={cn("w-full justify-start text-xs", isDark ? "hover:bg-white/10" : "hover:bg-gray-100")}
                              >
                                {startingSidebar === msg.user?.user_id ? (
                                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                ) : (
                                  <MessageSquareText className="h-3.5 w-3.5 mr-2" />
                                )}
                                Start Sidebar
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          <AvatarImage src={msg.user?.picture} />
                          <AvatarFallback className="bg-white/10 text-[10px]">
                            {msg.user?.name?.charAt(0) || (msg.user_id === 'bonita' ? 'B' : '?')}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className={cn(
                        "max-w-[70%]",
                        msg.user_id === user?.user_id && "text-right"
                      )}>
                        <p className={cn("text-[10px] mb-1", isDark ? "text-white/40" : "text-gray-500")}>
                          {msg.user_id === 'bonita' ? 'Bonita' : msg.user?.name}
                          <span className="ml-2 font-mono">{formatTime(msg.created_at)}</span>
                        </p>
                        <div className={cn(
                          "p-3 text-sm rounded-lg",
                          msg._status === 'failed' && "opacity-60"
                        )} style={
                          msg.user_id === user?.user_id
                            ? { background: 'linear-gradient(135deg,#F07B1D,#C85A12)', color: '#050608', borderRadius: '18px 18px 4px 18px' }
                            : msg.user_id === 'bonita'
                            ? { background: 'rgba(124,79,212,0.12)', border: '1px solid rgba(124,79,212,0.25)', color: 'var(--cream,#EAE0CC)', borderRadius: '18px 18px 18px 4px' }
                            : { background: '#1C2230', border: '1px solid rgba(255,255,255,0.05)', color: 'var(--cream,#EAE0CC)', borderRadius: '18px 18px 18px 4px' }
                        }>
                          {msg.content}
                        </div>
                        {/* Delivery status for own messages */}
                        {msg.user_id === user?.user_id && msg._status && (
                          <div className="flex items-center gap-1 mt-1 justify-end">
                            {msg._status === 'sending' && (
                              <span className={cn("text-[9px]", isDark ? "text-white/30" : "text-gray-400")}>Sending...</span>
                            )}
                            {msg._status === 'sent' && (
                              <span className="text-[9px] text-green-400">✓ Sent</span>
                            )}
                            {msg._status === 'failed' && (
                              <button
                                onClick={() => retryMessage(msg)}
                                className="text-[9px] text-red-400 hover:text-red-300 cursor-pointer bg-transparent border-none p-0"
                              >
                                Failed · Retry
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
              
              {/* Typing Indicator */}
              {typingUsers.length > 0 && (
                <div className={cn("text-[10px] italic mt-2", isDark ? "text-white/40" : "text-gray-500")}>
                  {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t border-white/10 flex-shrink-0">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={handleMessageChange}
                  placeholder="Type a message..."
                  className="flex-1 bg-transparent border-white/20 focus:border-white rounded-none text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  data-testid="gc-message-input"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={askBonitaInGC}
                  disabled={!newMessage.trim() || sendingMessage}
                  className={cn(isDark ? "text-white/50 hover:text-white" : "text-gray-500 hover:text-gray-900")}
                  title="Ask Bonita"
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
                <Button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  style={{ background: 'var(--fire,#F07B1D)', color: '#050608' }}
                  className="rounded-none hover:opacity-90"
                  data-testid="gc-send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center">
            <div className="text-center">
              <MessageCircle className={cn("h-12 w-12 mx-auto mb-4", isDark ? "text-white/20" : "text-gray-300")} />
              <p className={cn("text-sm", isDark ? "text-white/40" : "text-gray-500")}>Select a chat to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* Create GC Dialog - with User Selection */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border border-border sm:max-w-[450px] max-h-[80vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-white/10">
            <DialogTitle className="font-display text-sm tracking-widest uppercase">Start Chat</DialogTitle>
          </DialogHeader>
          
          <div className="p-4 space-y-4">
            {/* Chat Name */}
            <div>
              <label className={cn("text-[10px] uppercase tracking-wider mb-2 block", isDark ? "text-white/40" : "text-gray-500")}>Chat Name</label>
              <Input
                value={newGCName}
                onChange={(e) => setNewGCName(e.target.value)}
                placeholder="Name this conversation..."
                className="bg-transparent border-white/20 focus:border-white rounded-none"
                data-testid="gc-name-input"
              />
            </div>
            
            {/* User Search */}
            <div>
              <label className={cn("text-[10px] uppercase tracking-wider mb-2 block", isDark ? "text-white/40" : "text-gray-500")}>
                Add People {selectedUsers.length > 0 && `(${selectedUsers.length} selected)`}
              </label>
              <div className="relative">
                <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4", isDark ? "text-white/30" : "text-gray-400")} />
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search people..."
                  className="bg-transparent border-white/20 focus:border-white rounded-none pl-10"
                  data-testid="gc-user-search"
                />
              </div>
            </div>
            
            {/* Selected Users Chips */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(userId => {
                  const u = availableUsers.find(au => au.user_id === userId);
                  return (
                    <div 
                      key={userId}
                      className="flex items-center gap-1.5 px-2 py-1 bg-white/10 text-xs"
                    >
                      <span>@{u?.username || userId}</span>
                      <button 
                        onClick={() => toggleUserSelection(userId)}
                        className={cn(isDark ? "text-white/50 hover:text-white" : "text-gray-500 hover:text-gray-900")}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* User List */}
            <ScrollArea className="h-48 border border-white/10">
              {loadingUsers ? (
                <div className="p-4 space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className={cn("p-4 text-center text-sm", isDark ? "text-white/40" : "text-gray-500")}>
                  {userSearch ? 'No users found' : 'No other users yet'}
                </div>
              ) : (
                <div className="divide-y divide-white/10">
                  {filteredUsers.map((u) => (
                    <div
                      key={u.user_id}
                      onClick={() => toggleUserSelection(u.user_id)}
                      className={cn(
                        "flex items-center gap-3 p-3 cursor-pointer transition-colors",
                        selectedUsers.includes(u.user_id) ? "bg-white/10" : "hover:bg-white/5"
                      )}
                      data-testid={`gc-user-${u.user_id}`}
                    >
                      <div className={cn(
                        "w-5 h-5 border flex items-center justify-center transition-colors",
                        selectedUsers.includes(u.user_id) 
                          ? "bg-white border-white" 
                          : "border-white/30"
                      )}>
                        {selectedUsers.includes(u.user_id) && (
                          <Check className="h-3 w-3 text-black" />
                        )}
                      </div>
                      <Avatar className="h-10 w-10 border border-white/20">
                        <AvatarImage src={u.picture} />
                        <AvatarFallback className="bg-white/10 text-sm">
                          {u.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className={cn("font-medium text-sm truncate", textClass)}>{u.name}</p>
                        <p className={cn("text-xs truncate", isDark ? "text-white/40" : "text-gray-500")}>@{u.username}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            {/* Submit Button */}
            <Button
              onClick={createGC}
              disabled={!newGCName.trim() || selectedUsers.length === 0}
              className="w-full bg-white text-black hover:bg-white/90 rounded-none font-display tracking-wider"
              data-testid="create-gc-submit"
            >
              Start Chat with {selectedUsers.length} {selectedUsers.length === 1 ? 'Person' : 'People'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
